import { useCallback } from "react";
import CommonValidatorProps from "./commonProps";
import useValidator from "./useValidator";
import { useTranslation } from "react-i18next";

export interface NumberValidatorProps extends CommonValidatorProps<number> {
  required?: boolean;
  minValue?: number;
  maxValue?: number;
  positive?: boolean;
  notDecimal?: boolean;
}

const useNumberValidator = (props: NumberValidatorProps) => {
  const { t } = useTranslation();
  const { required, minValue, maxValue, positive, notDecimal } = props;

  const validateRequired = useCallback(
    (value?: number): [boolean, string] => {
      return [required ? value !== undefined : true, t("field:helper.fieldIsRequired")];
    },
    [required]
  );

  const validateMinValue = useCallback(
    (value?: number): [boolean, string] => {
      return [minValue ? value !== undefined && value >= minValue : true, `Min value : ${minValue}`];
    },
    [minValue]
  );

  const validateMaxValue = useCallback(
    (value?: number): [boolean, string] => {
      return [maxValue ? value !== undefined && value <= maxValue : true, `Max value : ${maxValue}`];
    },
    [maxValue]
  );

  const validateIsPositive = useCallback(
    (value?: number): [boolean, string] => {
      return [positive ? value !== undefined && value >= 0 : true, "Field must be positive"];
    },
    [positive]
  );

  const validateNotDecimal = useCallback(
    (value?: number): [boolean, string] => {
      return [notDecimal ? value !== undefined && value % 1 === 0 : true, "Field must be decimal"];
    },
    [notDecimal]
  );

  const validator = useValidator({
    ...props,
    validators: [validateRequired, validateMinValue, validateMaxValue, validateIsPositive, validateNotDecimal],
  });

  return validator;
};

export default useNumberValidator;
